import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  User as UserIcon, Building2, Users, CreditCard, MailPlus, Loader2,
  CheckCircle2, Trash2, Sparkles, X, HelpCircle,
} from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InviteDialog } from "@/components/InviteDialog";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { SETTINGS_GUIDE } from "@/lib/guides";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
function fmtAmount(cents, ccy = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(cents / 100);
}

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const { user, org, refresh } = useAuth();

  return (
    <div className="mx-auto max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="text-eyebrow text-fuchsia-400">Settings</div>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">Workspace settings</h1>
        <p className="mt-2 text-white/55">Manage your profile, organization, team, and billing.</p>
      </motion.div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-10">
        <TabsList className="flex w-full justify-start gap-2 rounded-full border border-white/10 bg-white/5 p-1">
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black" data-testid="settings-tab-profile">
            <UserIcon className="mr-2 h-3.5 w-3.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="organization" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black" data-testid="settings-tab-organization">
            <Building2 className="mr-2 h-3.5 w-3.5" /> Organization
          </TabsTrigger>
          <TabsTrigger value="team" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black" data-testid="settings-tab-team">
            <Users className="mr-2 h-3.5 w-3.5" /> Team
          </TabsTrigger>
          <TabsTrigger value="billing" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black" data-testid="settings-tab-billing">
            <CreditCard className="mr-2 h-3.5 w-3.5" /> Billing
          </TabsTrigger>
          <TabsTrigger value="guide" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-black" data-testid="settings-tab-guide">
            <HelpCircle className="mr-2 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-8"><ProfilePanel user={user} onSaved={refresh} /></TabsContent>
        <TabsContent value="organization" className="mt-8"><OrgPanel org={org} onSaved={refresh} /></TabsContent>
        <TabsContent value="team" className="mt-8"><TeamPanel currentUser={user} /></TabsContent>
        <TabsContent value="billing" className="mt-8"><BillingPanel org={org} onChanged={refresh} /></TabsContent>
        <TabsContent value="guide" className="mt-8">
          <ModuleGuide
            title={SETTINGS_GUIDE.title}
            whatItIs={SETTINGS_GUIDE.whatItIs}
            capabilities={SETTINGS_GUIDE.capabilities}
            howTo={SETTINGS_GUIDE.howTo}
            ctaLabel={SETTINGS_GUIDE.cta}
            onCta={() => setParams({ tab: "profile" })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Profile ----
function ProfilePanel({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => { setName(user?.name || ""); setEmail(user?.email || ""); }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, email });
      toast.success("Profile updated");
      onSaved?.();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password changed");
      setPwForm({ current: "", next: "" });
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-7" data-testid="profile-card">
        <h2 className="font-display text-2xl font-light">Your profile</h2>
        <p className="mt-1 text-sm text-white/55">Update your name and email.</p>
        <form onSubmit={save} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="profile-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Full name</Label>
            <Input className="border-white/10 bg-black/40 text-white" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
            <Input className="border-white/10 bg-black/40 text-white" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="profile-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
            </Button>
          </div>
        </form>
      </div>

      <div className="glass rounded-2xl p-7" data-testid="password-card">
        <h2 className="font-display text-2xl font-light">Change password</h2>
        <p className="mt-1 text-sm text-white/55">For email/password accounts only.</p>
        <form onSubmit={changePassword} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="password-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Current password</Label>
            <Input className="border-white/10 bg-black/40 text-white" type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="password-current" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">New password</Label>
            <Input className="border-white/10 bg-black/40 text-white" type="password" minLength={8} value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="password-new" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={pwSaving || !pwForm.current || !pwForm.next} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="password-save">
              {pwSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Org ----
function OrgPanel({ org, onSaved }) {
  const [name, setName] = useState(org?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(org?.name || ""); }, [org]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/orgs/current", { name });
      toast.success("Organization updated");
      onSaved?.();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-7" data-testid="org-card">
        <h2 className="font-display text-2xl font-light">Organization</h2>
        <p className="mt-1 text-sm text-white/55">Update your workspace name.</p>
        <form onSubmit={save} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="org-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Organization name</Label>
            <Input className="border-white/10 bg-black/40 text-white" value={name} onChange={(e) => setName(e.target.value)} data-testid="org-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-white/50">Plan & status</Label>
            <div className="flex h-9 items-center gap-2 rounded-md border border-white/10 bg-black/40 px-3 text-sm">
              <span className="capitalize">{org?.plan}</span>
              <span className="text-white/30">·</span>
              <span className="capitalize text-white/70">{org?.subscription_status}</span>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="org-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Team ----
function TeamPanel({ currentUser }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([api.get("/orgs/members"), api.get("/orgs/invites")]);
      setMembers(m.data); setInvites(i.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const removeInvite = async (id) => {
    try {
      await api.delete(`/orgs/invites/${id}`);
      toast.success("Invite revoked");
      fetchData();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const removeMember = async () => {
    if (!removing) return;
    try {
      await api.delete(`/orgs/members/${removing.user_id}`);
      toast.success("Member removed");
      setRemoving(null);
      fetchData();
    } catch (err) { toast.error(errorMessage(err)); setRemoving(null); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-7" data-testid="team-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-light">Team</h2>
            <p className="mt-1 text-sm text-white/55">Manage members and pending invites.</p>
          </div>
          <Button onClick={() => setInviteOpen(true)} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="team-invite-cta">
            <MailPlus className="mr-2 h-4 w-4" /> Invite teammate
          </Button>
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-white/5">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-white/40">Member</TableHead>
                <TableHead className="text-white/40">Role</TableHead>
                <TableHead className="text-white/40">Joined</TableHead>
                <TableHead className="text-white/40"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={4} className="text-center text-white/40"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
              )}
              {!loading && members.map((m) => (
                <TableRow key={m.user_id} className="border-white/5" data-testid={`member-row-${m.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        {m.avatar_url && <AvatarImage src={m.avatar_url} />}
                        <AvatarFallback className="bg-white/10 text-xs">{(m.name || m.email).slice(0,2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm">{m.name}</div>
                        <div className="text-xs text-white/45">{m.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize text-white/75">{m.role}</TableCell>
                  <TableCell className="text-white/55">{fmtDate(m.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {m.user_id !== currentUser?.id && m.role !== "owner" && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setRemoving(m)}
                        className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        data-testid={`member-remove-${m.user_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="glass rounded-2xl p-7" data-testid="invites-card">
        <h3 className="font-display text-xl font-light">Pending invites</h3>
        {(!invites || invites.length === 0) ? (
          <p className="mt-3 text-sm text-white/45">No pending invites.</p>
        ) : (
          <div className="mt-4 overflow-hidden rounded-xl border border-white/5">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-white/40">Email</TableHead>
                  <TableHead className="text-white/40">Role</TableHead>
                  <TableHead className="text-white/40">Expires</TableHead>
                  <TableHead className="text-white/40"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((i) => (
                  <TableRow key={i.id} className="border-white/5" data-testid={`invite-row-${i.id}`}>
                    <TableCell className="text-white/85">{i.email}</TableCell>
                    <TableCell className="capitalize text-white/75">{i.role}</TableCell>
                    <TableCell className="text-white/55">{fmtDate(i.expires_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeInvite(i.id)} className="text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`invite-revoke-${i.id}`}>
                        <X className="h-3.5 w-3.5" /> Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvited={fetchData} />
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.name} from the workspace?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              They will lose access immediately. You can invite them back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeMember} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-remove-member">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- Billing (real Stripe) ----
function BillingPanel({ org, onChanged }) {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [resuming, setResuming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([api.get("/billing/status"), api.get("/billing/invoices")]);
      setStatus(s.data); setInvoices(i.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [org?.id]);

  // Handle Stripe return: success → poll for ~12s until webhook flips status; cancel → toast.
  const stripeParam = params.get("stripe");
  const stripeSessionId = params.get("session_id");
  useEffect(() => {
    if (!stripeParam) return;
    if (stripeParam === "cancel") {
      toast("Checkout cancelled. No changes were made.");
      params.delete("stripe"); setParams(params, { replace: true });
      return;
    }
    if (stripeParam === "success") {
      toast.success("Payment received — finalising your subscription…");
      let attempts = 0;
      const max = 12;
      const tick = async () => {
        attempts++;
        try {
          const s = await api.get("/billing/status");
          setStatus(s.data);
          const subbed = s.data?.stripe_subscription_id && ["active", "trialing"].includes(s.data?.subscription_status);
          if (subbed) {
            const i = await api.get("/billing/invoices");
            setInvoices(i.data);
            toast.success("You're on Pro. Welcome aboard.");
            onChanged?.();
            params.delete("stripe"); params.delete("session_id");
            setParams(params, { replace: true });
            return;
          }
        } catch (_) {}
        if (attempts < max) setTimeout(tick, 1000);
        else {
          toast("Webhook is taking a moment. Refresh in a few seconds.");
          params.delete("stripe"); params.delete("session_id");
          setParams(params, { replace: true });
        }
      };
      setTimeout(tick, 800);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [stripeParam, stripeSessionId]);

  const startCheckout = async () => {
    setRedirecting(true);
    try {
      const r = await api.post("/billing/create-checkout-session", { origin_url: window.location.origin });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
      else throw new Error("No checkout URL returned");
    } catch (err) {
      toast.error(errorMessage(err));
      setRedirecting(false);
    }
  };

  const openPortal = async () => {
    setRedirecting(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (r.data?.portal_url) window.location.href = r.data.portal_url;
      else throw new Error("No portal URL returned");
    } catch (err) {
      toast.error(errorMessage(err));
      setRedirecting(false);
    }
  };

  const cancel = async () => {
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription will end at the end of the current period.");
      setConfirmCancel(false);
      await load(); onChanged?.();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.post("/billing/resume");
      toast.success("Subscription resumed — no further changes will be made.");
      await load(); onChanged?.();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setResuming(false); }
  };

  const subStatus = status?.subscription_status;
  const isSubscribed = !!status?.stripe_subscription_id && ["active", "trialing", "past_due"].includes(subStatus);
  const willCancel = !!status?.cancel_at_period_end;
  const isCanceled = subStatus === "canceled" && !isSubscribed;
  const inTrial = subStatus === "trialing";
  const isPastDue = subStatus === "past_due";

  return (
    <div className="space-y-6">
      <div className="glass-heavy relative overflow-hidden rounded-2xl p-7" data-testid="billing-overview">
        <div className="absolute inset-x-0 top-0 h-0.5 brand-bg" />
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-eyebrow text-white/40">Current plan</div>
            <div className="font-display mt-1 text-3xl font-light tracking-tight">
              {isSubscribed ? (
                <>Pro · <span className="brand-text">{fmtAmount(status?.monthly_total_cents ?? 9900)}/mo</span></>
              ) : (
                <>Free trial {status && <span className="text-2xl text-white/45">· {status.days_left_in_trial} days left</span>}</>
              )}
            </div>
            <div className="mt-1 text-sm text-white/55">
              {isSubscribed ? (
                <>
                  <span data-testid="billing-seat-line">{status?.seat_count || 1} seat{(status?.seat_count || 1) > 1 ? "s" : ""}</span>
                  {inTrial && <> · Trial ends {fmtDate(status?.current_period_end || status?.trial_ends_at)}</>}
                  {!inTrial && willCancel && <> · Cancels on {fmtDate(status?.current_period_end)}</>}
                  {!inTrial && !willCancel && <> · Next billing date: {fmtDate(status?.current_period_end || status?.next_billing_date)}</>}
                  {isPastDue && <> · <span className="text-amber-300">Payment failed — please update your card</span></>}
                </>
              ) : isCanceled ? (
                <>Canceled on {fmtDate(status?.canceled_at)}. Subscribe again any time.</>
              ) : (
                <>{status?.seat_count || 1} seat{(status?.seat_count || 1) > 1 ? "s" : ""} on trial. Upgrade anytime to keep going.</>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="billing-actions">
            {!isSubscribed && (
              <div className="flex w-full flex-col gap-2">
                <Button onClick={startCheckout} disabled={redirecting} className="self-start rounded-full bg-white text-black hover:bg-zinc-200" data-testid="upgrade-button">
                  {redirecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Upgrade to Pro — {fmtAmount(status?.monthly_total_cents ?? 9900)}/mo
                </Button>
                <p className="text-xs text-white/40" data-testid="promo-hint">
                  Have a promo code? You'll be able to enter it on the next page.
                </p>
              </div>
            )}
            {isSubscribed && status?.portal_available && (
              <Button onClick={openPortal} disabled={redirecting} variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="portal-button">
                {redirecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Manage subscription
              </Button>
            )}
            {isSubscribed && willCancel && (
              <Button onClick={resume} disabled={resuming} className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid="resume-button">
                {resuming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Resume subscription
              </Button>
            )}
            {isSubscribed && !willCancel && (
              <Button onClick={() => setConfirmCancel(true)} variant="ghost" className="rounded-full text-white/55 hover:text-white" data-testid="cancel-button">
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Seats card */}
      <div className="glass rounded-2xl p-7" data-testid="seats-card">
        <h3 className="font-display text-xl font-light">Seats</h3>
        <p className="mt-1 text-sm text-white/55">
          Base plan covers 1 seat. Each additional teammate is {fmtAmount(status?.per_seat_cents ?? 2900)}/month.
          {isSubscribed && " Adding or removing members syncs to Stripe automatically with prorated billing."}
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="text-eyebrow text-white/40">Current seats</div>
            <div className="font-display mt-1 text-3xl font-light" data-testid="seats-current">{status?.seat_count ?? 1}</div>
            <div className="mt-1 text-xs text-white/45">{(status?.additional_seats ?? 0)} additional · 1 base</div>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="text-eyebrow text-white/40">Per additional seat</div>
            <div className="font-display mt-1 text-3xl font-light">{fmtAmount(status?.per_seat_cents ?? 2900)}<span className="text-sm text-white/40">/mo</span></div>
          </div>
          <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4">
            <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Monthly total</div>
            <div className="font-display mt-1 text-3xl font-light text-white" data-testid="seats-total">{fmtAmount(status?.monthly_total_cents ?? 9900)}<span className="text-sm text-white/45">/mo</span></div>
            <div className="mt-1 text-xs text-indigo-700 dark:text-indigo-200/80">
              {fmtAmount(status?.base_price_cents ?? 9900)}
              {(status?.additional_seats ?? 0) > 0 && <> + {status.additional_seats} × {fmtAmount(status?.per_seat_cents ?? 2900)}</>}
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-7" data-testid="invoices-card">
        <h3 className="font-display text-xl font-light">Invoices</h3>
        {loading ? (
          <p className="mt-3 text-sm text-white/45"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</p>
        ) : (!invoices || invoices.length === 0) ? (
          <p className="mt-3 text-sm text-white/45">No invoices yet. Upgrade to Pro to start a paid billing cycle.</p>
        ) : (
          <div className="mt-4 overflow-hidden rounded-xl border border-white/5">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-white/40">Date</TableHead>
                  <TableHead className="text-white/40">Description</TableHead>
                  <TableHead className="text-white/40">Status</TableHead>
                  <TableHead className="text-right text-white/40">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-white/5" data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="text-white/75">{fmtDate(inv.invoice_date)}</TableCell>
                    <TableCell className="text-white/85">
                      {inv.hosted_invoice_url ? (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">
                          {inv.description}
                        </a>
                      ) : (
                        inv.description
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300">
                        <CheckCircle2 className="h-3 w-3" /> {inv.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-white">{fmtAmount(inv.amount_cents, inv.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Our Promise reassurance — visible to all users (trial + active) */}
      <div
        className="rounded-2xl border border-indigo-500/15 bg-gradient-to-br from-indigo-500/[0.04] via-transparent to-teal-500/[0.04] p-6"
        data-testid="billing-promise-card"
      >
        <div className="text-eyebrow text-indigo-300">A few things we want you to know:</div>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-white/70">
          <p>
            <strong className="font-medium text-white">No contracts.</strong> Cancel anytime &mdash; you stay active until end of period.
          </p>
          <p>
            <strong className="font-medium text-white">No late fees. No collections.</strong> Short on cash? Email{" "}
            <a
              href="mailto:info@symplee.ai?subject=I%20need%20to%20pause%20my%20Symplee%20subscription"
              className="text-indigo-300 underline-offset-2 hover:underline"
              data-testid="billing-promise-mailto"
            >
              info@symplee.ai
            </a>{" "}
            and we&rsquo;ll pause it. We&rsquo;ve all been there.
          </p>
          <p>
            <strong className="font-medium text-white">Your price is locked in for life.</strong> If we ever change our public pricing, you stay grandfathered.
          </p>
        </div>
      </div>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white" data-testid="cancel-intercept-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sorry to see you go.</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-white/65">
              <span className="block">
                No hard feelings. You&rsquo;ll stay active until the end of your billing period
                ({fmtDate(status?.current_period_end || status?.next_billing_date)}), then we&rsquo;ll stop
                charging &mdash; no further action needed.
              </span>
              <span className="block">
                If it&rsquo;s a cash-flow issue, just email{" "}
                <a href="mailto:info@symplee.ai" className="text-indigo-300 underline-offset-2 hover:underline">
                  info@symplee.ai
                </a>{" "}
                &mdash; we can pause it instead. No questions, no awkwardness.
              </span>
              <span className="block">
                If something&rsquo;s broken, please tell us at{" "}
                <a href="mailto:info@symplee.ai" className="text-indigo-300 underline-offset-2 hover:underline">
                  info@symplee.ai
                </a>{" "}
                &mdash; we&rsquo;d love a chance to fix it.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <a
              href="mailto:info@symplee.ai?subject=I%20need%20to%20pause%20my%20Symplee%20subscription"
              className="inline-flex items-center justify-center rounded-md border border-white/15 bg-transparent px-4 py-2 text-sm text-white/75 hover:bg-white/5"
              data-testid="cancel-intercept-email-us"
            >
              Email us instead
            </a>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid="cancel-intercept-keep">
              Keep Pro
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={cancel}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="confirm-cancel-subscription"
            >
              Cancel my subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
